import React, { Fragment } from 'react'
import p from 'prop-types'
import styles from './Form.scss'
import cx from 'classnames'
import _ from 'lodash'
import Textarea from 'react-textarea-autosize'
import Select from 'react-select'
import { CTA } from 'common/styleguide/CTA'
import { RadioGroup } from 'common/styleguide/RadioGroup'
import { DroppablePage } from 'common/DroppablePage'
import { CircleSpinner } from 'common/spinners'
import { photoGear } from 'lib/urlHelpers'
import { Icon } from 'common/Icon'
import { OrderedMap } from 'immutable'

export class Form extends React.PureComponent {
  static propTypes = {
    onSave: p.func.isRequired,
    onCancel: p.func.isRequired,
    onLocationSearch: p.func.isRequired,
    poi: p.object.isRequired,
    tags: p.array.isRequired,
    errors: p.object,
    combinePois: p.array
  }

  constructor (props) {
    super(props)
    const { poi, routes, combinePois } = props
    this.state = {
      searchLocation: '',
      name: poi.name,
      description: poi.description,
      url: poi.url,
      tagNames: poi.tagNames,
      routesSelected: _.fromPairs(routes.map(r => ([r.id, true]))),
      combineInto: combinePois ? `${combinePois[0].id}` : undefined
    }
  }

  componentDidUpdate (prevProps, prevState) {
    const { poi } = this.props
    if (prevProps.poi && (prevProps.poi.id !== poi.id)) {
      this.setState({
        name: poi.name,
        description: poi.description,
        url: poi.url,
        tagNames: poi.tagNames,
        photoIds: poi.photoIds
      })
      this.props.unsetBounds() // force map recenter
    }
  }

  onTriggerFileBrowser = () => {
    this._dropzone && this._dropzone.open()
  }

  onNameChange = ({ target: { value: name } }) => {
    this.setState({ name })
  }

  onDescriptionChange = ({ target: { value: description } }) => {
    this.setState({ description })
  }

  onUrlChange = ({ target: { value: url } }) => {
    this.setState({ url })
  }

  onCombineIntoChange = (combineInto) => {
    this.props.fetchPoi(combineInto)
    this.setState({ combineInto })
  }

  onSearchLocationChange = ({ target: { value: searchLocation } }) => {
    this.setState({ searchLocation })
  }

  onSearchLocationKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.props.onLocationSearch(this.state.searchLocation)
    }
  }

  onTagNamesChange = (values) => {
    const tagNames = values.map(v => v.value)
    this.setState({ tagNames })
  }

  onSelectedRouteChange = (e) => {
    this.setState({
      routesSelected: { ...this.state.routesSelected, [+e.target.value]: e.target.checked }
    })
  }

  poiState = () => {
    const { name, description, url, tagNames } = this.state
    return { name, description, url, tagNames }
  }

  onSubmit = (event) => {
    const { combinePois } = this.props
    const { routesSelected } = this.state
    event.preventDefault()
    this.props.onSave({
      poi: this.poiState(),
      routeIds: Object.keys(routesSelected).filter(id => routesSelected[id]),
      poiIds: combinePois && combinePois.map(p => p.id)
    })
  }

  render () {
    const { combinePois, routes, tags, onCancel, photoIds, onPhotosDrop, onPhotoRemoved, onFileRemoved, files, pendingUploads } = this.props
    const { routesSelected, name, description, url, tagNames, combineInto } = this.state

    const tagValues = tags.map(t => ({ value: t, label: t }))
    const poiTagNames = tagNames.map(t => ({ value: t, label: t }))

    const errors = (this.props.errors && this.props.errors.toObject()) || {}
    if (errors.lat || errors.lng) {
      errors.base || (errors.base = [])
      errors.base.push('You must choose a location for the POI')
    }

    const combineOpts = OrderedMap(combinePois?.reduce((acc, p) => { acc[p.id] = p.name; return acc }, {}))
    const filesPlusPhotos = files.size + photoIds.size
    return (
      <div className={styles.formContainer}>
        <DroppablePage
          ref={r => { this._dropzone = r }}
          multiple
          onDrop={onPhotosDrop}>
          <form className={styles.form} onSubmit={this.onSubmit}>
            <div className={styles.formContents}>
              {errors.base && <div className={styles.formErrors}>{errors.base}</div>}
              {combinePois && <div className={cx(styles.formGroup, styles.combinePois)}>
                <label>Combine POIs into</label>
                <RadioGroup
                  name='combinePois'
                  options={combineOpts}
                  selected={combineInto}
                  onChange={this.onCombineIntoChange} />
              </div>}
              <div className={styles.formGroup}>
                <label>Jump to a Location</label>
                <div className={styles.locationInput}>
                  <input onChange={this.onSearchLocationChange} onKeyPress={this.onSearchLocationKeyPress} />
                  <span className='icon icon-search' />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>
                  Name *
                  {errors.name && <span className={styles.errorMsg}>{errors.name}</span>}
                </label>
                <div className={cx(styles.name, errors.name && styles.errorHighlight)}>
                  <input value={name} onChange={this.onNameChange} />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Tags</label>
                <div className={styles.tagsInput}>
                  <Select.Creatable
                    multi
                    joinValues
                    delimiter=','
                    value={poiTagNames}
                    onChange={this.onTagNamesChange}
                    options={tagValues}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                {errors.url && <div className={styles.errorMsg}>{errors.url}</div>}
                <label>URL</label>
                <div className={styles.urlInput}>
                  <input value={url} onChange={this.onUrlChange} />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Description</label>
                <div className={styles.descriptionInput}>
                  <Textarea value={description} onChange={this.onDescriptionChange} />
                </div>
              </div>
              <div className={styles.photos}>
                {filesPlusPhotos === 0 && !pendingUploads && <Fragment>
                  <h2>Photos</h2>
                  <div>Drag photos onto this page or <a onClick={this.onTriggerFileBrowser}>click here</a> to add photos to this POI.</div>
                </Fragment>}
                {pendingUploads > 0 && <p><CircleSpinner size={16} /> Uploading {pendingUploads} photo{pendingUploads > 1 ? 's' : ''}...</p>}
                <div className={styles.photoGrid}>
                  {photoIds.map(id => (
                    <div className={styles.photo} key={id}>
                      <div className={styles.removePhoto} onClick={() => onPhotoRemoved(id)}>
                        <Icon size={20} i='ios-close' />
                      </div>
                      <img src={photoGear(id)} />
                    </div>
                  ))}
                  {files.map((file, idx) => (
                    <div className={styles.photo} key={file.size}>
                      <div className={styles.removePhoto} onClick={e => onFileRemoved(e, idx)}>
                        <Icon size={20} i='ios-close' />
                      </div>
                      <img src={file.preview} />
                    </div>
                  ))}
                </div>
                {filesPlusPhotos > 0 && <div className={styles.morePhotos}>
                  <a onClick={this.onTriggerFileBrowser} data-tip title='You can also add photos by dragging them onto this page'>Add photos</a>
                </div>}
              </div>
              {routes && routes.length > 0 && !combinePois && <div className={styles.formGroup}>
                <label>Routes</label>
                {routes.map(r => (
                  <label key={r.id} className={styles.checkboxLabel}>
                    <input type='checkbox' checked={routesSelected[r.id]} onChange={this.onSelectedRouteChange} value={r.id} />
                    <a href={`/routes/${r.id}`} target='showRoute'>{r.name}</a>
                  </label>
                ))}
              </div>}
            </div>
            <div className={styles.actions}>
              <CTA link onClick={onCancel} margins={false}>
                Cancel
              </CTA>
              <CTA button type='submit' margins={false}>
                Save
              </CTA>
            </div>
          </form>
        </DroppablePage>
      </div>
    )
  }
}
